/**
 * lib/shopifyClient.js
 * Shopify Admin API istemcisi — dev proxy veya serverless proxy üzerinden çalışır.
 *
 * Dev:  vite.config.js server.proxy '/shopify-api' → https://<shop>/admin/api/<ver>
 *       (Access token vite proxy'de header olarak enjekte edilir, client'a sızmaz.)
 * Prod: /api/shopify-proxy (Vercel serverless, SHOPIFY_ACCESS_TOKEN env)
 */

import { getAccessToken } from '@/api/adapters/SupabaseAdapter';

const IS_DEV = import.meta.env.DEV;

/** Oturum token'ı ile Authorization header'ı üretir (proxy auth zorunlu). */
async function authHeaders() {
  const token = await getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Shopify REST kaynaklarını çek
 * @param {string} resource - 'orders' | 'products' | 'customers'
 * @param {object} params   - ek query parametreleri
 */
async function shopifyFetch(resource, params = {}) {
  const query = new URLSearchParams({ resource, ...params });
  const url = IS_DEV
    ? `/shopify-api/${resource}.json?${new URLSearchParams(params)}`
    : `/api/shopify-proxy?${query}`;

  const res = await fetch(url, { headers: await authHeaders() });

  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Shopify API Error (${res.status}): ${err}`);
  }

  return res.json();
}

// ─── GraphQL ───────────────────────────────────────────────────────────────

const GRAPHQL_URL = IS_DEV ? '/shopify-api/graphql.json' : '/api/shopify-proxy';

/**
 * Shopify Admin GraphQL çağrısı (proxy üzerinden).
 * @param {string} query     - GraphQL sorgusu
 * @param {object} variables - GraphQL değişkenleri
 * @returns {Promise<object>} data
 */
export async function shopifyGraphQL(query, variables = {}) {
  const res = await fetch(GRAPHQL_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ query, variables }),
  });

  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Shopify GraphQL Error (${res.status}): ${err}`);
  }

  const json = await res.json();
  if (json.errors?.length) {
    throw new Error(`Shopify GraphQL: ${json.errors.map((e) => e.message).join('; ')}`);
  }
  return json.data;
}

const ORDERS_QUERY = /* GraphQL */ `
  query SyncOrders($first: Int!, $after: String, $query: String) {
    orders(first: $first, after: $after, query: $query, sortKey: CREATED_AT) {
      pageInfo { hasNextPage endCursor }
      nodes {
        id
        name
        legacyResourceId
        createdAt
        updatedAt
        cancelledAt
        closedAt
        displayFinancialStatus
        displayFulfillmentStatus
        email
        note
        paymentGatewayNames
        totalPriceSet { shopMoney { amount currencyCode } }
        subtotalPriceSet { shopMoney { amount } }
        totalTaxSet { shopMoney { amount } }
        totalShippingPriceSet { shopMoney { amount } }
        customer { firstName lastName email phone }
        shippingAddress { name company address1 address2 zip city country phone }
        billingAddress { name company }
        lineItems(first: 50) {
          nodes {
            title
            sku
            quantity
            variantTitle
            variant { id }
            discountedUnitPriceSet { shopMoney { amount } }
          }
        }
      }
    }
  }
`;

/**
 * Shopify'daki TÜM siparişleri cursor sayfalamasıyla çeker.
 * @param {object} opts
 * @param {string|null} opts.updatedAtMin - ISO tarih; verilirse artımlı senkron
 * @param {number} opts.pageSize          - sayfa boyutu (max 50 önerilir)
 * @param {(fetched: number) => void} opts.onProgress
 * @returns {Promise<Array<object>>} GraphQL order node listesi
 */
export async function fetchAllShopifyOrders({ updatedAtMin = null, pageSize = 50, onProgress } = {}) {
  const all = [];
  let after = null;
  let hasNext = true;
  // PIM P5: FieldConnect'ten Shopify'a aynalanan siparişler (Order-Mirror)
  // ASLA geri içeri alınmaz — çifte kayıt/stok döngüsü kilidi.
  const MIRROR_EXCLUDE = '-tag:fieldconnect-mirror';
  const search = updatedAtMin
    ? `${MIRROR_EXCLUDE} AND updated_at:>='${updatedAtMin}'`
    : MIRROR_EXCLUDE;

  while (hasNext) {
    const data = await shopifyGraphQL(ORDERS_QUERY, {
      first: pageSize,
      after,
      query: search,
    });
    const conn = data?.orders;
    if (!conn) break;

    all.push(...conn.nodes);
    if (typeof onProgress === 'function') onProgress(all.length);

    hasNext = conn.pageInfo?.hasNextPage;
    after = conn.pageInfo?.endCursor || null;

    // API throttle'a saygı — sayfalar arası kısa bekleme
    if (hasNext) await new Promise((r) => setTimeout(r, 350));
  }

  return all;
}

// ─── Orders ────────────────────────────────────────────────────────────────

/**
 * Tüm siparişleri getir
 * @param {object} opts
 * @param {string} opts.status   - 'any' | 'open' | 'closed' | 'cancelled'
 * @param {number} opts.limit    - max 250
 * @param {string} opts.page_info - cursor tabanlı sayfalama
 */
export async function getOrders({ status = 'any', limit = 50, page_info } = {}) {
  const params = { status, limit };
  if (page_info) params.page_info = page_info;
  const data = await shopifyFetch('orders', params);
  return data.orders || [];
}

/**
 * Tek sipariş getir
 */
export async function getOrder(orderId) {
  const data = await shopifyFetch(`orders/${orderId}`, {});
  return data.order;
}

// ─── Products ──────────────────────────────────────────────────────────────

export async function getProducts({ limit = 50 } = {}) {
  const data = await shopifyFetch('products', { limit });
  return data.products || [];
}

// ─── Customers ─────────────────────────────────────────────────────────────

export async function getCustomers({ limit = 50 } = {}) {
  const data = await shopifyFetch('customers', { limit });
  return data.customers || [];
}

// ─── Order count ───────────────────────────────────────────────────────────

export async function getOrderCount({ status = 'any' } = {}) {
  const data = await shopifyFetch('orders/count', { status });
  return data.count || 0;
}

// ─── Fulfillment status çevirisi ───────────────────────────────────────────

export function formatFulfillmentStatus(status) {
  const map = {
    fulfilled: 'Versendet',
    unfulfilled: 'Ausstehend',
    partial: 'Teilweise',
    restocked: 'Zurückgelagert',
    null: 'Ausstehend',
  };
  return map[status] || status || 'Unbekannt';
}

export function formatFinancialStatus(status) {
  const map = {
    paid: 'Bezahlt',
    pending: 'Ausstehend',
    partially_paid: 'Teilbezahlt',
    refunded: 'Erstattet',
    voided: 'Storniert',
    partially_refunded: 'Teilerstattet',
  };
  return map[status] || status || 'Unbekannt';
}

export function getStatusColor(financialStatus, fulfillmentStatus) {
  if (financialStatus === 'paid' && fulfillmentStatus === 'fulfilled') return 'green';
  if (financialStatus === 'paid' && fulfillmentStatus === 'unfulfilled') return 'yellow';
  if (financialStatus === 'pending') return 'orange';
  if (financialStatus === 'refunded' || financialStatus === 'voided') return 'red';
  return 'gray';
}
